'use client';

import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';

import { HtmlDescription } from '@eliadmin/core/src/components/ui/html-description';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export function ProductDescription({ product }: { product: ProductDetailData }) {
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const { t } = useTranslations();

  const descriptionToRender = selectedVariant?.description || product.description;

  return (
    <section
      id="description-parameters"
      className={cn(
        'custom-section',
        'border-t border-t-[#EBEBEB] pb-6 pt-12 sm:pb-10 sm:pt-16 lg:pb-12 lg:pt-24',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 sm:gap-8 md:flex-row md:justify-between',
        )}
      >
        <div className="flex w-full max-w-[709px] flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
          <h2 className="text-lg font-semibold text-foreground sm:text-xl lg:text-2xl">
            {t('text231')}
          </h2>
          <HtmlDescription
            color="muted-foreground"
            content={descriptionToRender}
            size="sm"
            className={cn('w-full', 'ea-product-description__html-description')}
          />
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-4 rounded-[6px] bg-[#F4F5FB] p-4 sm:gap-5 sm:p-5 md:max-w-[499px] lg:gap-6 lg:p-6">
          <h2 className="text-lg font-semibold text-foreground sm:text-xl lg:text-2xl">
            {t('text3180')}
          </h2>
          <div className="flex w-full flex-col items-start justify-start gap-0">
            <div className="grid w-full grid-cols-2 gap-0 border-b border-b-[#EBEBEB] py-2">
              <p className="col-span-1 text-sm font-light text-[#666464]">{t('text2481')}</p>
              <p className="col-span-1 text-right text-sm font-light text-foreground">
                {product.code}
              </p>
            </div>
            {product.has_variants
              ? selectedVariant?.values
                  ?.filter(
                    param =>
                      typeof param.attribute_name === 'string' &&
                      param.attribute_name.trim() !== '' &&
                      typeof param.name === 'string' &&
                      param.name.trim() !== '',
                  )
                  .map(param => (
                    <div
                      className="grid w-full grid-cols-2 gap-0 border-b border-b-[#EBEBEB] py-2"
                      key={`${param.attribute_name}-${param.name}`}
                    >
                      <p className="col-span-1 text-sm font-light text-[#666464]">
                        {param.attribute_name}
                      </p>
                      <p className="col-span-1 text-right text-sm font-light text-foreground">
                        {param.name}
                      </p>
                    </div>
                  ))
              : product.attributes
                  ?.filter(
                    attr =>
                      typeof attr.name === 'string' &&
                      attr.name.trim() !== '' &&
                      attr.values &&
                      attr.values.length > 0,
                  )
                  .map(attr => (
                    <div
                      className="grid w-full grid-cols-2 gap-0 border-b border-b-[#EBEBEB] py-2"
                      key={attr.id}
                    >
                      <p className="col-span-1 text-sm font-light text-[#666464]">{attr.name}</p>
                      <p className="col-span-1 text-right text-sm font-light text-foreground">
                        {attr.values?.map(v => v.name).join(', ')}
                      </p>
                    </div>
                  ))}
          </div>
        </div>
      </div>
    </section>
  );
}
